import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  TextField,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  CircularProgress
} from '@material-ui/core'

const ITEM_TYPES = ['all', 'standard', 'lump_sum', 'allowance', 'header', 'subtotal', 'informational']
const MAPPING_STATUSES = ['all', 'unmapped', 'pending', 'mapped', 'needs_review', 'ignored']

const ITEM_TYPE_CHIP = {
  standard:      { color: 'default', style: {} },
  lump_sum:      { color: 'primary', style: {} },
  allowance:     { color: 'secondary', style: {} },
  header:        { color: 'default', style: { fontStyle: 'italic' } },
  subtotal:      { color: 'default', style: { fontStyle: 'italic' } },
  informational: { color: 'default', style: { fontStyle: 'italic' } }
}

const MAPPING_STATUS_CHIP = {
  mapped:       { color: 'default', style: { backgroundColor: '#4caf50', color: '#fff' } },
  pending:      { color: 'default', style: { backgroundColor: '#ff9800', color: '#fff' } },
  unmapped:     { color: 'default', style: {} },
  needs_review: { color: 'default', style: { backgroundColor: '#f44336', color: '#fff' } },
  ignored:      { color: 'default', style: { backgroundColor: '#9e9e9e', color: '#fff' } }
}

const getItemTypeChip = (type) => ITEM_TYPE_CHIP[type] || { color: 'default', style: {} }
const getMappingStatusChip = (status) => MAPPING_STATUS_CHIP[status] || { color: 'default', style: {} }

// Inline editable cell — click to edit, save on blur or Enter
// renderDisplay: optional render fn for non-editing state (e.g. show a Chip)
const EditableCell = ({ value, onSave, type, options, renderDisplay }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const startEdit = () => {
    setDraft(value)
    setEditing(true)
  }

  const commit = () => {
    setEditing(false)
    if (draft !== value) onSave(draft)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commit()
    if (e.key === 'Escape') setEditing(false)
  }

  if (!editing) {
    if (renderDisplay) {
      return (
        <span onClick={startEdit} style={{ cursor: 'pointer' }} title='Click to edit'>
          {renderDisplay(value)}
        </span>
      )
    }
    return (
      <span
        onClick={startEdit}
        style={{ cursor: 'pointer', display: 'inline-block', minWidth: 40 }}
        title='Click to edit'
      >
        {value !== undefined && value !== null && value !== '' ? String(value) : <em style={{ color: '#bbb' }}>—</em>}
      </span>
    )
  }

  if (type === 'select' && options) {
    return (
      <Select
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        autoFocus
        style={{ minWidth: 120 }}
      >
        {options.map(opt => (
          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
        ))}
      </Select>
    )
  }

  return (
    <TextField
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      autoFocus
      size='small'
      type={type === 'number' ? 'number' : 'text'}
      style={{ minWidth: 80 }}
    />
  )
}

const ItemsTab = ({ packageId, url, onItemUpdate }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterGroup, setFilterGroup] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')

  useEffect(() => {
    if (!packageId) return
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}/items`)
      .then(res => {
        setItems(res.data || [])
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load bid items.')
        setLoading(false)
      })
  }, [packageId, url])

  const handleFieldSave = async (itemId, field, value) => {
    try {
      const payload = { [field]: value }
      const res = await axios.put(`${url}/api/bid-estimation/bid-items/${itemId}`, payload)
      const updated = res.data
      setItems(prev => prev.map(it => it._id === itemId ? { ...it, ...updated } : it))
      if (onItemUpdate) onItemUpdate(updated)
    } catch (err) {
      console.error('Failed to update bid item:', err)
    }
  }

  // Distinct group names for filter dropdown
  const groupNames = ['all', ...Array.from(
    new Set(items.map(it => it.bidGroup?.name).filter(Boolean))
  ).sort()]

  const needsReviewCount = items.filter(it => it.mappingStatus === 'needs_review').length

  const filtered = items.filter(it => {
    if (filterGroup !== 'all' && it.bidGroup?.name !== filterGroup) return false
    if (filterType !== 'all' && it.itemType !== filterType) return false
    if (filterStatus !== 'all' && it.mappingStatus !== filterStatus) return false
    return true
  })

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Typography color='error'>{error}</Typography>
      </div>
    )
  }

  return (
    <div>
      {/* Warning badge */}
      {needsReviewCount > 0 && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: '#fff3e0',
          border: '1px solid #ffb300',
          borderRadius: 4,
          padding: '6px 12px',
          marginBottom: 12
        }}>
          <Typography variant='body2' style={{ color: '#e65100' }}>
            <strong>{needsReviewCount}</strong> item{needsReviewCount !== 1 ? 's' : ''} need{needsReviewCount === 1 ? 's' : ''} review
          </Typography>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <FormControl size='small' variant='outlined' style={{ minWidth: 160 }}>
          <InputLabel>Group</InputLabel>
          <Select
            value={filterGroup}
            onChange={e => setFilterGroup(e.target.value)}
            label='Group'
          >
            {groupNames.map(g => (
              <MenuItem key={g} value={g}>{g === 'all' ? 'All Groups' : g}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' variant='outlined' style={{ minWidth: 160 }}>
          <InputLabel>Item Type</InputLabel>
          <Select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            label='Item Type'
          >
            {ITEM_TYPES.map(t => (
              <MenuItem key={t} value={t}>{t === 'all' ? 'All Types' : t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' variant='outlined' style={{ minWidth: 180 }}>
          <InputLabel>Mapping Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            label='Mapping Status'
          >
            {MAPPING_STATUSES.map(s => (
              <MenuItem key={s} value={s}>{s === 'all' ? 'All Statuses' : s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant='caption' color='textSecondary' style={{ alignSelf: 'center' }}>
          {filtered.length} of {items.length} items
        </Typography>
      </div>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Group</TableCell>
              <TableCell>#</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align='right'>Qty</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Mapping Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align='center'>
                  <Typography variant='body2' style={{ padding: 20, color: '#999' }}>
                    No items match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(item => {
                const isNeedsReview = item.mappingStatus === 'needs_review'
                const typeChip = getItemTypeChip(item.itemType)
                const statusChip = getMappingStatusChip(item.mappingStatus)

                return (
                  <TableRow
                    key={item._id}
                    hover
                    style={isNeedsReview ? { backgroundColor: '#fff3e0' } : {}}
                  >
                    {/* Group */}
                    <TableCell>
                      <Typography variant='caption' color='textSecondary'>
                        {item.bidGroup?.name || '—'}
                      </Typography>
                    </TableCell>

                    {/* # (sequence number) */}
                    <TableCell>
                      <Typography variant='caption' style={{ fontFamily: 'monospace' }}>
                        {item.sequenceNumber != null ? item.sequenceNumber : item.lineNumber || '—'}
                      </Typography>
                    </TableCell>

                    {/* Description — inline editable */}
                    <TableCell>
                      <EditableCell
                        value={item.description || ''}
                        onSave={val => handleFieldSave(item._id, 'description', val)}
                      />
                    </TableCell>

                    {/* Unit — inline editable */}
                    <TableCell>
                      <EditableCell
                        value={item.unit || ''}
                        onSave={val => handleFieldSave(item._id, 'unit', val)}
                      />
                    </TableCell>

                    {/* Qty — inline editable */}
                    <TableCell align='right'>
                      <EditableCell
                        value={item.quantity != null ? item.quantity : ''}
                        type='number'
                        onSave={val => handleFieldSave(item._id, 'quantity', val === '' ? null : Number(val))}
                      />
                    </TableCell>

                    {/* Type — inline editable select, shows Chip when not editing */}
                    <TableCell>
                      <EditableCell
                        value={item.itemType || ''}
                        type='select'
                        options={ITEM_TYPES.filter(t => t !== 'all')}
                        onSave={val => handleFieldSave(item._id, 'itemType', val)}
                        renderDisplay={(val) => {
                          if (!val) return <em style={{ color: '#bbb' }}>—</em>
                          const chip = getItemTypeChip(val)
                          return (
                            <Chip
                              label={val}
                              size='small'
                              color={chip.color}
                              style={chip.style}
                            />
                          )
                        }}
                      />
                    </TableCell>

                    {/* Mapping Status chip */}
                    <TableCell>
                      <Chip
                        label={item.mappingStatus || 'unmapped'}
                        size='small'
                        color={statusChip.color}
                        style={statusChip.style}
                      />
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default ItemsTab
